import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import Tooltip from '@mui/material/Tooltip';
import ClearIcon from '@mui/icons-material/Clear';
import Divider from '@mui/material/Divider';
import KeyIcon from '@mui/icons-material/Key';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';

/**
 * Component for displaying and managing the details of an API key. Allows viewing,
 * creating, and updating API keys.
 *
 * @component
 *
 * @example
 * <ApiKeyDetails apiKey={apiKey} onSubmit={handleSubmit} />
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.usageLimit - The usage limit object to be displayed and edited.
 * @param {Function} props.onSubmit - The function to call when the form is submitted.
 * @param {Function} props.onFieldChange - The function to call when the fields value change.
 * @param {Array<Object>} props.assistants - The list of available assistants to choose from.
 *
 * @returns {React.Component} The Usage Limit Details component.
 */
function UsageLimitDetails({ usageLimit, onSubmit, onFieldChange, assistants }) {
  const { formatMessage: msg } = useIntl();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty }
  } = useForm({
    defaultValues: {
      id: '',
      assistantId: '',
      period: 'MONTH',
      maxInputTokens: '',
      maxOutputTokens: '',
      maxRequests: '',
      notificationMail: ''
    }
  });

  watch();

  // useEffect to reset the form when usageLimit prop changes
  useEffect(() => {
    if (usageLimit) {
      // Set form values if usageLimit exists
      reset({
        id: usageLimit.id || '',
        assistantId: usageLimit.assistant?.id || '', // Set assistantId from usageLimit.assistant.id
        period: usageLimit.period || 'MONTH',
        maxInputTokens: usageLimit.maxInputTokens || '',
        maxOutputTokens: usageLimit.maxOutputTokens || '',
        maxRequests: usageLimit.maxRequests || '',
        notificationMail: usageLimit.notificationMail || ''
      });
    } else {
      reset({
        id: '',
        assistantId: '',
        period: 'MONTH',
        maxInputTokens: '',
        maxOutputTokens: '',
        maxRequests: '',
        notificationMail: ''
      });
    }
  }, [usageLimit, reset]);

  useEffect(() => {
    if (onFieldChange) {
      onFieldChange(isDirty);
    }
  }, [isDirty, onFieldChange]);

  const handleReset = useCallback(() => {
    if (usageLimit?.id) {
      reset(usageLimit);
    } else {
      reset({
        id: '',
        period: 'MONTH',
        maxInputTokens: '',
        maxOutputTokens: '',
        maxRequests: '',
        notificationMail: ''
      });
    }
  }, [usageLimit, reset]);

  const clearField = (fieldName) => {
    setValue(fieldName, '');
  };

  const isCreating = !usageLimit?.id;

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      display="flex"
      direction="column"
      sx={{ height: '100%' }}
    >
      <Stack display="flex" direction="row">
        <KeyIcon color="text" fontSize="medium" sx={{ mr: '0.75rem' }} />
        <MDTypography variant="h5">
          {msg({
            id: isCreating
              ? 'page.usage.limits.details.creation.title'
              : 'page.usage.limits.details.modification.title'
          })}
        </MDTypography>
      </Stack>
      <Divider sx={{ mt: '0.5rem', mb: '0.75rem' }} />
      <Stack
        spacing={1}
        display="flex"
        direction="column"
        flex={1}
        overflow="auto"
        mb={2}
        sx={{ scrollbarWidth: 'thin' }}
      >
        {!isCreating && (
          <>
            <Controller
              name="id"
              control={control}
              render={({ field }) => (
                <MDInput
                  {...field}
                  label={msg({ id: 'field.id.label' })}
                  variant="filled"
                  fullWidth
                  required
                  size="small"
                  InputProps={{
                    readOnly: true
                  }}
                />
              )}
            />
          </>
        )}
        {/* New Assistant Select */}
        <Controller
          name="assistantId"
          control={control}
          render={({ field }) => (
            <FormControl variant="filled" fullWidth size="small" error={!!errors.assistantId}>
              <InputLabel>{msg({ id: 'field.assistant.label' })}</InputLabel>
              <Select {...field} label={msg({ id: 'field.assistant.label' })} displayEmpty>
                <MenuItem value="">Limite globale</MenuItem>
                {assistants.map((assistant) => (
                  <MenuItem key={assistant.id} value={assistant.id}>
                    {assistant.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.assistantId && <FormHelperText>{errors.assistantId.message}</FormHelperText>}
            </FormControl>
          )}
        />
        {/* Existing Period Select */}
        <Controller
          name="period"
          control={control}
          rules={{ required: msg({ id: 'form.error.required' }) }}
          render={({ field }) => (
            <FormControl variant="filled" fullWidth size="small" error={!!errors.period}>
              <InputLabel>{msg({ id: 'field.usage.limits.period.label' })}</InputLabel>
              <Select {...field} label={msg({ id: 'field.usage.limits.period.label' })}>
                <MenuItem value="DAY">Jour</MenuItem>
                <MenuItem value="WEEK">Semaine</MenuItem>
                <MenuItem value="MONTH">Mois</MenuItem>
              </Select>
              {errors.period && <FormHelperText>{errors.period.message}</FormHelperText>}
            </FormControl>
          )}
        />
        <Controller
          name="maxInputTokens"
          control={control}
          render={({ field }) => (
            <MDInput
              {...field}
              label={msg({ id: 'field.usage.limits.maxInputTokens.label' })}
              variant="filled"
              fullWidth
              size="small"
              type="number"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('maxInputTokens')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <Controller
          name="maxOutputTokens"
          control={control}
          render={({ field }) => (
            <MDInput
              {...field}
              label={msg({ id: 'field.usage.limits.maxOutputTokens.label' })}
              variant="filled"
              fullWidth
              size="small"
              type="number"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('maxOutputTokens')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />{' '}
        <Controller
          name="maxRequests"
          control={control}
          render={({ field }) => (
            <MDInput
              {...field}
              label={msg({ id: 'field.usage.limits.maxRequests.label' })}
              variant="filled"
              fullWidth
              size="small"
              type="number"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('maxRequests')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <Controller
          name="notificationMail"
          control={control}
          rules={{ required: msg({ id: 'validation.required' }) }}
          render={({ field }) => (
            <MDInput
              {...field}
              required
              label={msg({ id: 'field.usage.limits.mail.label' })}
              error={!!errors.notificationMail}
              helperText={errors.notificationMail?.message}
              variant="filled"
              fullWidth
              size="small"
              type="string"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('notificationMail')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
      </Stack>
      <Divider sx={{ mt: 0 }} />
      <Stack direction="row" spacing={2} sx={{ marginTop: 'auto !important' }}>
        <MDButton type="button" fullWidth color="secondary" onClick={handleReset}>
          {msg({ id: 'button.reset' })}
        </MDButton>
        <MDButton type="submit" fullWidth color="primary">
          {isCreating ? msg({ id: 'button.create' }) : msg({ id: 'button.update' })}
        </MDButton>
      </Stack>
    </Stack>
  );
}

UsageLimitDetails.propTypes = {
  usageLimit: PropTypes.shape({
    id: PropTypes.number,
    assistant: PropTypes.shape({
      // Add assistant shape
      id: PropTypes.string,
      label: PropTypes.string
    }),
    maxInputTokens: PropTypes.number,
    maxOutputTokens: PropTypes.number,
    maxRequests: PropTypes.number,
    period: PropTypes.string,
    notificationMail: PropTypes.string
  }),
  onSubmit: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  assistants: PropTypes.arrayOf(
    PropTypes.shape({
      // Add assistants propTypes
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired
};

export default UsageLimitDetails;
